import React, { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import ForkliftCard from "@/components/ForkliftCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Search, Pencil, X, Save } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestStatusPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const prefilledId = searchParams.get("id") || "";

  const [requestId, setRequestId] = useState(prefilledId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchRequest = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/customer-requests/${id}`);
      setData(res);
      setEditForm(res.request);
    } catch {
      toast.error("Request not found");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (prefilledId) fetchRequest(prefilledId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledId]);

  const handleCheck = (e) => {
    e.preventDefault();
    fetchRequest(requestId);
  };

  const handleEditChange = (key, value) => setEditForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    try {
      const body = {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        capacity_min: editForm.capacity_min || null,
        capacity_max: editForm.capacity_max || null,
        engine_type: editForm.engine_type || null,
        mast_stages: editForm.mast_stages || null,
        tire_type: editForm.tire_type || null,
        drive: editForm.drive || null,
        condition: editForm.condition || null,
        budget_max: editForm.budget_max || null,
        notes: editForm.notes,
      };
      const { data: res } = await axios.put(`${API}/customer-requests/${data.request.id}`, body);
      setData(res);
      setEditing(false);
      toast.success(t.request_status.updated);
    } catch {
      toast.error("Error updating request");
    }
  };

  return (
    <div data-testid="request-status-page" className="pt-24 pb-16 min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Search box */}
        {!data && (
          <div className="text-center">
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-gray-900 mb-2">
              {t.request_status.check_title}
            </h1>
            <p className="text-base text-gray-600 mb-8">{t.request_status.check_subtitle}</p>
            <form onSubmit={handleCheck} className="flex gap-3 max-w-md mx-auto" data-testid="check-request-form">
              <Input
                data-testid="check-request-input"
                placeholder={t.request_status.request_id}
                value={requestId}
                onChange={(e) => setRequestId(e.target.value)}
                className="bg-white flex-1"
              />
              <button
                type="submit"
                data-testid="check-request-btn"
                className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-5 py-2 rounded-sm"
              >
                <Search className="w-4 h-4" /> {t.request_status.check}
              </button>
            </form>
          </div>
        )}

        {loading && (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        )}

        {data && !loading && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-2xl font-bold text-gray-900">{t.request_status.title}</h1>
              <div className="flex items-center gap-3">
                <Badge className="bg-yellow-400 text-gray-900 border-none hover:bg-yellow-400">
                  {data.request.status}
                </Badge>
                {!editing ? (
                  <button
                    data-testid="edit-request-btn"
                    onClick={() => setEditing(true)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    <Pencil className="w-4 h-4" /> {t.request_status.edit}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      data-testid="save-request-btn"
                      onClick={handleSave}
                      className="inline-flex items-center gap-1.5 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 px-3 py-1.5 rounded-sm text-sm"
                    >
                      <Save className="w-3.5 h-3.5" /> {t.request_status.save}
                    </button>
                    <button
                      data-testid="cancel-edit-btn"
                      onClick={() => { setEditing(false); setEditForm(data.request); }}
                      className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-3.5 h-3.5" /> {t.request_status.cancel}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Request details */}
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-6 mb-8">
              {editing ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="col-span-2 md:col-span-3">
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.name}</label>
                    <Input value={editForm.name || ""} onChange={(e) => handleEditChange("name", e.target.value)} className="bg-white" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.email}</label>
                    <Input value={editForm.email || ""} onChange={(e) => handleEditChange("email", e.target.value)} className="bg-white" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.phone}</label>
                    <Input value={editForm.phone || ""} onChange={(e) => handleEditChange("phone", e.target.value)} className="bg-white" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.condition}</label>
                    <Select value={editForm.condition || "any"} onValueChange={(v) => handleEditChange("condition", v === "any" ? null : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t.inventory.condition_any}</SelectItem>
                        <SelectItem value="new">{t.inventory.condition_new}</SelectItem>
                        <SelectItem value="used">{t.inventory.condition_used}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.engine_type}</label>
                    <Select value={editForm.engine_type || "any"} onValueChange={(v) => handleEditChange("engine_type", v === "any" ? null : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t.inventory.engine_any}</SelectItem>
                        <SelectItem value="propane">{t.inventory.engine_propane}</SelectItem>
                        <SelectItem value="diesel">{t.inventory.engine_diesel}</SelectItem>
                        <SelectItem value="electric">{t.inventory.engine_electric}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.mast_stages}</label>
                    <Select value={editForm.mast_stages ? String(editForm.mast_stages) : "any"} onValueChange={(v) => handleEditChange("mast_stages", v === "any" ? null : parseInt(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t.inventory.mast_any}</SelectItem>
                        <SelectItem value="2">{t.inventory.mast_2}</SelectItem>
                        <SelectItem value="3">{t.inventory.mast_3}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.tire_type}</label>
                    <Select value={editForm.tire_type || "any"} onValueChange={(v) => handleEditChange("tire_type", v === "any" ? null : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t.inventory.tire_any}</SelectItem>
                        <SelectItem value="pneumatic">{t.inventory.tire_pneumatic}</SelectItem>
                        <SelectItem value="cushion">{t.inventory.tire_cushion}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.drive}</label>
                    <Select value={editForm.drive || "any"} onValueChange={(v) => handleEditChange("drive", v === "any" ? null : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t.inventory.drive_any}</SelectItem>
                        <SelectItem value="2wd">{t.inventory.drive_2wd}</SelectItem>
                        <SelectItem value="4wd">{t.inventory.drive_4wd}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.budget}</label>
                    <Input type="number" value={editForm.budget_max || ""} onChange={(e) => handleEditChange("budget_max", e.target.value ? parseFloat(e.target.value) : null)} className="bg-white" />
                  </div>
                  <div className="col-span-2 md:col-span-3">
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.notes}</label>
                    <Textarea value={editForm.notes || ""} onChange={(e) => handleEditChange("notes", e.target.value)} className="bg-white" />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <Detail label={t.request.name} value={data.request.name} />
                  <Detail label={t.request.email} value={data.request.email} />
                  <Detail label={t.request.phone} value={data.request.phone || "—"} />
                  <Detail label={t.request.condition} value={data.request.condition || "Any"} />
                  <Detail label={t.request.engine_type} value={data.request.engine_type || "Any"} />
                  <Detail label={t.request.mast_stages} value={data.request.mast_stages ? `${data.request.mast_stages} stages` : "Any"} />
                  <Detail label={t.request.tire_type} value={data.request.tire_type || "Any"} />
                  <Detail label={t.request.drive} value={data.request.drive || "Any"} />
                  <Detail label={t.request.budget} value={data.request.budget_max ? `$${data.request.budget_max.toLocaleString()}` : "—"} />
                  {data.request.capacity_min && <Detail label="Capacity Min" value={`${data.request.capacity_min} lbs`} />}
                  {data.request.capacity_max && <Detail label="Capacity Max" value={`${data.request.capacity_max} lbs`} />}
                </div>
              )}
            </div>

            {/* Matches */}
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">
              {t.request_status.matches} ({data.match_count})
            </h2>
            {data.matches.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.matches.map((f) => (
                  <ForkliftCard key={f.id} forklift={f} />
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-8">
                {t.request_status.no_matches}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-gray-900 font-medium capitalize">{value}</p>
    </div>
  );
}
